"use client";

import { motion, AnimatePresence } from "framer-motion";
import { PRIZES } from "../../utils/prizes";

const XP_RANGES: Record<string, [number, number]> = {
  xp_blue: [100, 2000],
  xp_purple: [500, 5000],
  xp_gold: [1000, 10000],
  xp_black: [5000, 10000],
};

const FIXED_POOLS: Record<string, number[]> = {
  gamblor_bonus: [1, 2, 5, 10, 20],
  gamblor_spins: [5, 10, 15, 20, 25],
  astr: [10, 20, 50, 100, 200],
  astr_hh: [14000],
};

interface LootboxPrizeModalProps {
  show: boolean;
  onClose: () => void;
  selectedBox: {
    id: string;
    name: string;
    accent: string;
  };
}

export default function LootboxPrizeModal({
  show,
  onClose,
  selectedBox,
}: LootboxPrizeModalProps) {
  const filteredPrizes = PRIZES.filter((p) => p.boxes.includes(selectedBox.id));

  const getRangeLabel = (type: string) => {
    if (XP_RANGES[type]) {
      const [min, max] = XP_RANGES[type];
      return `${min.toLocaleString()} – ${max.toLocaleString()} XP`;
    }
    if (FIXED_POOLS[type]) {
      const min = Math.min(...FIXED_POOLS[type]);
      const max = Math.max(...FIXED_POOLS[type]);
      if (type === "astr_hh") return `${max} ASTR`;
      if (type === "astr") return `${min} – ${max} ASTR`;
      if (type === "gamblor_spins") return `${min} – ${max} SPINS`;
      if (type === "gamblor_bonus") return `${min} – ${max} BONUS`;
    }
    return "";
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
        >
          <motion.div
            className="relative w-[92%] max-w-6xl max-h-[85vh] bg-[#0b0c10]/95 border border-white/10 rounded-lg p-6 md:p-10 overflow-y-auto shadow-2xl scrollbar-hide"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", damping: 18, stiffness: 180 }}
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors text-2xl"
            >
              ✕
            </button>

            <div className="text-center mb-10">
              <p
                className="uppercase tracking-[0.25em] text-[12px]"
                style={{ color: selectedBox.accent }}
              >
                What’s inside
              </p>
              <h2
                className="text-3xl md:text-5xl font-extrabold uppercase mt-2"
                style={{
                  background: `linear-gradient(90deg, ${selectedBox.accent}, #ffffff)`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                {selectedBox.name} Prizes
              </h2>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-5 md:gap-6 justify-items-center">
              {filteredPrizes.map((prize) => (
                <motion.div
                  key={prize.id}
                  className="relative bg-black/40 border border-white/10 rounded-md aspect-square overflow-hidden group transition-all w-full max-w-[130px] sm:max-w-[150px] md:max-w-[170px] lg:max-w-[190px]"
                  whileHover={{ scale: 1.05 }}
                  transition={{ type: "spring", stiffness: 220, damping: 18 }}
                  style={{
                    boxShadow: `0 0 0px ${prize.accent}`,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = `0 0 20px ${prize.accent}`;
                    e.currentTarget.style.borderColor = prize.accent;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = `0 0 0px ${prize.accent}`;
                    e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)";
                  }}
                >
                  <img
                    src={prize.image}
                    alt={prize.name}
                    className="absolute inset-0 object-cover w-full h-full opacity-90 group-hover:opacity-100 transition-opacity duration-300"
                  />

                  {/* Name + Range Overlay */}
                  <div className="absolute bottom-0 left-0 right-0 bg-black/70 py-1.5 text-center">
                    <p
                      className="text-[11px] uppercase font-semibold tracking-wider leading-tight"
                      style={{ color: prize.accent }}
                    >
                      {prize.name}
                    </p>
                    {getRangeLabel(prize.type) && (
                      <p className="text-[10px] font-mono text-gray-400 tracking-tight">
                        {getRangeLabel(prize.type)}
                      </p>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
